/**
 * React Query hooks for Training Capability Taxonomy
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { TrainingCapability, ArtifactCapabilityMapRow, CapabilityKey } from '@/constants/trainingCapabilities';

/**
 * Fetch all active capabilities
 */
export function useTrainingCapabilities() {
  return useQuery({
    queryKey: ['training-capabilities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_capabilities')
        .select('*')
        .eq('is_active', true)
        .order('cluster')
        .order('sort_order');

      if (error) throw error;
      return (data || []) as unknown as TrainingCapability[];
    },
    staleTime: 5 * 60 * 1000, // capabilities rarely change
  });
}

/**
 * Fetch capability mappings for a single artifact
 */
export function useArtifactCapabilityMappings(artifactId: string | null) {
  return useQuery({
    queryKey: ['artifact-capability-mappings', artifactId],
    queryFn: async () => {
      if (!artifactId) return [];
      const { data, error } = await supabase
        .from('training_artifact_capability_map')
        .select('*')
        .eq('artifact_id', artifactId);

      if (error) throw error;
      return (data || []) as unknown as ArtifactCapabilityMapRow[];
    },
    enabled: !!artifactId,
  });
}

/**
 * Upsert a capability mapping (insert or update weight/is_primary)
 */
export function useUpsertCapabilityMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      artifactId: string;
      capabilityKey: CapabilityKey;
      weight?: number;
      isPrimary?: boolean;
    }) => {
      const { data: existing } = await supabase
        .from('training_artifact_capability_map')
        .select('id')
        .eq('artifact_id', params.artifactId)
        .eq('capability_key', params.capabilityKey)
        .maybeSingle();

      if (existing) {
        const update: Record<string, unknown> = {};
        if (params.weight !== undefined) update.weight = params.weight;
        if (params.isPrimary !== undefined) update.is_primary = params.isPrimary;
        const { error } = await supabase
          .from('training_artifact_capability_map')
          .update(update)
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('training_artifact_capability_map')
          .insert({
            artifact_id: params.artifactId,
            capability_key: params.capabilityKey,
            weight: params.weight ?? 1.0,
            is_primary: params.isPrimary ?? false,
          });
        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['artifact-capability-mappings', variables.artifactId] });
      queryClient.invalidateQueries({ queryKey: ['artifact-capability-map-counts'] });
    },
  });
}

/**
 * Delete a capability mapping
 */
export function useDeleteCapabilityMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { mappingId: string; artifactId: string }) => {
      const { error } = await supabase
        .from('training_artifact_capability_map')
        .delete()
        .eq('id', params.mappingId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['artifact-capability-mappings', variables.artifactId] });
      queryClient.invalidateQueries({ queryKey: ['artifact-capability-map-counts'] });
    },
  });
}

/**
 * Fetch capability mapping counts per artifact (for catalog table badges)
 */
export function useArtifactCapabilityMapCounts() {
  return useQuery({
    queryKey: ['artifact-capability-map-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_artifact_capability_map')
        .select('artifact_id');

      if (error) throw error;

      const counts = new Map<string, number>();
      data?.forEach(row => {
        counts.set(row.artifact_id, (counts.get(row.artifact_id) || 0) + 1);
      });
      return counts;
    },
  });
}
